import { ColumnTable } from 'arquero'
import { PinnedMorphotype, PlotSelectionState, MergedMorphotype, CellPlotDatum } from 'redux/slices/types'
import { getColumn } from './arrow/arqueroHelpers'

type OptimizedPlotSelectionState = PlotSelectionState & {
  cellIds?: string[]
}

type OptimizedMorphotype = Omit<PinnedMorphotype, 'cells' | 'transposedCells'> & {
  cells: OptimizedPlotSelectionState
  transposedCells?: OptimizedPlotSelectionState
}

type OptimizedMergedMorphotype = Omit<MergedMorphotype, 'cells'> & {
  cells: OptimizedPlotSelectionState
}

// Helper function to compress a single PlotSelectionState by storing only cellIds
function compressPlotSelectionState(
  plotState: PlotSelectionState | undefined
): OptimizedPlotSelectionState | undefined {
  if (!plotState) return undefined
  
  return {
    range: plotState.range,
    lassoPoints: plotState.lassoPoints,
    selections: plotState.selections,
    cellIds: plotState.points?.map(p => p.cellId).filter((id): id is string => Boolean(id)) || [],
  }
}

// Helper function to build cell coordinate map from DataFrame
function buildCellCoordinateMap(dataFrame: ColumnTable | undefined): Map<string, Omit<CellPlotDatum, 'cellId'>> {
  const cellCoordMap = new Map<string, Omit<CellPlotDatum, 'cellId'>>()

  if (!dataFrame) return cellCoordMap

  const cellIds = getColumn<string>(dataFrame, 'CELL_ID')
  const umapX = getColumn<string | number>(dataFrame, 'UMAP_0')
  const umapY = getColumn<string | number>(dataFrame, 'UMAP_1')

  if (cellIds.length === 0) return cellCoordMap

  for (let i = 0; i < cellIds.length; i += 1) {
    const x = Number(umapX[i])
    const y = Number(umapY[i])
    if (!Number.isNaN(x) && !Number.isNaN(y)) {
      cellCoordMap.set(cellIds[i], { x, y, originalX: x, originalY: y })
    }
  }

  return cellCoordMap
}

// Helper function to reconstruct a single PlotSelectionState
function reconstructPlotSelectionState(
  plotState: OptimizedPlotSelectionState | undefined,
  cellCoordMap: Map<string, Omit<CellPlotDatum, 'cellId'>>
): PlotSelectionState | undefined {
  if (!plotState) return undefined
  
  const hasOptimizedFormat = plotState.cellIds && !plotState.points
  
  if (hasOptimizedFormat) {
    // Reconstruct points from cellIds
    const reconstructedPoints = plotState.cellIds?.map((cellId: string) => {
      const coords = cellCoordMap.get(cellId)
      return coords ? { ...coords, cellId } : { x: 0, y: 0, originalX: 0, originalY: 0, cellId }
    }) || []

    return {
      points: reconstructedPoints,
      range: plotState.range,
      lassoPoints: plotState.lassoPoints,
      selections: plotState.selections
    }
  }

  // Return as-is if already in full format
  return plotState as PlotSelectionState
}

/**
 * Compress morphotypes by storing only cellIds instead of full coordinate data
 */
function compressMorphotypeCoordinates(
  morphotypes: PinnedMorphotype[] | undefined,
  keepSnapshots = true
): OptimizedMorphotype[] | undefined {
  if (!morphotypes) return undefined
  
  return morphotypes.map(morphotype => ({
    id: morphotype.id,
    name: morphotype.name,
    active: morphotype.active,
    isClusterBased: morphotype.isClusterBased,
    leidenCluster: morphotype.leidenCluster,
    cells: compressPlotSelectionState(morphotype.cells),
    transposedCells: compressPlotSelectionState(morphotype.transposedCells),
    cellInfoGroupsSnapshot: keepSnapshots ? morphotype.cellInfoGroupsSnapshot : undefined,
    comparisonDimensionsSnapshot: keepSnapshots ? morphotype.comparisonDimensionsSnapshot : undefined
  })) as OptimizedMorphotype[]
}

/**
 * Compress pinned morphotypes by storing only cellIds instead of full coordinate data
 * Keeps snapshots for analysis and comparison features
 */
export function compressPinnedMorphotypes(
  morphotypes: PinnedMorphotype[] | undefined
): OptimizedMorphotype[] | undefined {
  return compressMorphotypeCoordinates(morphotypes, true)
}

/**
 * Compress morphotypes to remove by storing only cellIds instead of full coordinate data  
 * Removes snapshots since they're not needed for cell filtering
 */
export function compressMorphotypesToRemove(
  morphotypes: PinnedMorphotype[] | undefined
): OptimizedMorphotype[] | undefined {
  return compressMorphotypeCoordinates(morphotypes, false)
}

/**
 * Compress merged morphotypes by storing only cellIds instead of full coordinate data
 */
export function compressMergedMorphotypes(
  mergedMorphotypes: MergedMorphotype[] | undefined
): OptimizedMergedMorphotype[] | undefined {
  if (!mergedMorphotypes) return undefined
  
  return mergedMorphotypes.map(morphotype => ({
    id: morphotype.id,
    cells: compressPlotSelectionState(morphotype.cells)
  })) as OptimizedMergedMorphotype[]
}

/**
 * Reconstruct coordinates for morphotypes that have optimized storage format.
 * Uses DataFrame for memory efficiency.
 * Takes morphotypes with cellIds and reconstructs the full coordinate data from DataFrame.
 */
export default function reconstructMorphotypeCoordinates(
  morphotypes: (PinnedMorphotype | OptimizedMorphotype)[],
  dataFrame: ColumnTable | undefined
): PinnedMorphotype[] {
  const cellCoordMap = buildCellCoordinateMap(dataFrame)

  if (!dataFrame || cellCoordMap.size === 0) {
    return morphotypes as PinnedMorphotype[]
  }

  return morphotypes.map(morphotype => {
    const reconstructedCells = reconstructPlotSelectionState(
      morphotype.cells as OptimizedPlotSelectionState,
      cellCoordMap
    )

    const reconstructedTransposedCells = reconstructPlotSelectionState(
      (morphotype as OptimizedMorphotype).transposedCells,
      cellCoordMap
    )

    return {
      ...morphotype,
      cells: reconstructedCells || morphotype.cells,
      transposedCells: reconstructedTransposedCells || (morphotype as PinnedMorphotype).transposedCells
    } as PinnedMorphotype
  })
}

/**
 * Reconstruct coordinates for merged morphotypes.
 * Uses DataFrame for memory efficiency.
 * Takes merged morphotypes with cellIds and reconstructs the full coordinate data from DataFrame.
 */
export function reconstructMergedMorphotypeCoordinates(
  mergedMorphotypes: (MergedMorphotype | OptimizedMergedMorphotype)[] | undefined,
  dataFrame: ColumnTable | undefined
): MergedMorphotype[] | undefined {
  if (!mergedMorphotypes) return undefined

  const cellCoordMap = buildCellCoordinateMap(dataFrame)

  if (!dataFrame || cellCoordMap.size === 0) {
    return mergedMorphotypes as MergedMorphotype[]
  }

  return mergedMorphotypes.map(mergedMorphotype => {
    const reconstructedCells = reconstructPlotSelectionState(
      mergedMorphotype.cells as OptimizedPlotSelectionState,
      cellCoordMap
    )

    return {
      ...mergedMorphotype,
      cells: reconstructedCells || mergedMorphotype.cells
    } as MergedMorphotype
  })
}